import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Checkbox,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
    SelectedProductMutasi,
    ParamsGetItem,
    ParamsGetKategori,
    IOptions,
} from "../../../constants/types";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import useItem from "../../../services/queries/useItem";
import useKategori from "../../../services/queries/useKategori";
import useUser from "../../../services/queries/useUser";
import toRupiah from "../../../utils/toRupiah";
import Spinner from "../../Spinner/Spinner";
import SearchIcon from "@mui/icons-material/Search";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import { useAuthStore } from "../../../stores/authStore";
import NoRowsImage from "../../NoRowsImage/NoRowsImage";
import LoadingTabel from "../../LoadingTabel/LoadingTabel";
import { useTheme } from "@mui/material/styles";

interface IPilihItemMutasiStepProps {
    addProducts: (item: SelectedProductMutasi[]) => void;
}

const PilihItemMutasiStep = ({ addProducts }: IPilihItemMutasiStepProps) => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { data: user, status: statusUser } = useUser();
    const ukmIdUser = useAuthStore((s) => s.ukmIdUser);

    const initialParamsStok: ParamsGetItem = {
        ukmId: ukmIdUser && ukmIdUser[0],
        search: "",
        kategori: "",
        isShow: 0,
        size: 10,
        page: 1,
    };

    const initialParamsKategori: ParamsGetKategori = {
        ukmId: ukmIdUser && ukmIdUser[0],
        search: "",
        size: 500,
        page: 1,
    };

    const [paramsStok, setParamsStok] = useState(initialParamsStok);
    const [paramsKategori, setParamsKategori] = useState(initialParamsKategori);
    const [search, setSearch] = useState<string>("");
    const [selectedKategori, setSelectedKategori] = useState<IOptions | null>(
        null,
    );

    const { data: stok, isLoading: isLoadingStok } = useItem(paramsStok);
    const {
        data: kategori,
        status: statusKategori,
        isLoading: isLoadingKategori,
    } = useKategori(paramsKategori);

    const [selectedProducts, setSelectedProducts] = useState<
        SelectedProductMutasi[]
    >([]);

    const stokAllProduct = useMemo(() => {
        if (stok && stok.content.length > 0) {
            return stok.content
                ?.filter((item) => item.stok !== null)
                .map((list) => ({
                    id: list.id,
                    nama: list.namaBarang,
                    sku: list.sku,
                    harga: list.harga,
                    stok: list.stok,
                    hpp: list.hpp,
                }));
        }
        // setSelectedProducts([]);
    }, [stok]);

    const isSelected = (id: number) => {
        const filteredProduct = selectedProducts.filter(
            (li) => li.id === id,
        ).length;
        if (filteredProduct > 0) {
            return true;
        } else {
            return false;
        }
    };

    const handleClickAllProduct = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                console.log(event.target.checked);
                setSelectedProducts(stokAllProduct ?? []);
                return;
            }
            return setSelectedProducts([]);
        },
        [stokAllProduct],
    );

    const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleSearch = () => {
        setParamsStok((prev) => ({
            ...prev,
            search: search,
        }));
    };

    const handleSelectedProducts = (
        id: number,
        nama: string,
        sku: string,
        harga: number,
        stok: number,
        hpp: {
            id: number;
            faktur: string;
            hargaBeli: number;
            jumlah: number;
        }[],
    ) => {
        if (isSelected(id)) {
            const newList = selectedProducts.filter((li) => li.id !== id);
            setSelectedProducts(newList);
        } else {
            setSelectedProducts((prev) => [
                ...prev,
                {
                    id,
                    nama,
                    sku,
                    harga,
                    stok,
                    hpp,
                },
            ]);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "namaBarang",
            headerName: "Nama Barang",
            width: 300,
            flex: 2,
            minWidth: 150,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="table-cell-trucate">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "kategori",
            headerName: "Kategori",
            headerAlign: "center",
            align: "center",
            // width: 150,
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
                <Tooltip title={params.value.nama}>
                    <span className="table-cell-trucate">
                        {params.value.nama}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "harga",
            headerName: "Harga",
            headerAlign: "center",
            align: "right",
            // type: "number",
            // width: 150,
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="table-cell-trucate">
                        {toRupiah(params.value ?? 0)}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "sku",
            headerName: "SKU",
            headerAlign: "center",
            align: "center",
            // type: "number",
            // width: 150,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="table-cell-trucate">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "stok",
            headerName: "Stok",
            headerAlign: "center",
            align: "center",
            // type: "number",
            // width: 150,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip
                    title={params.value === null ? "\u221e" : params.value}
                >
                    <span className="table-cell-trucate">
                        {params.value === null ? "\u221e" : params.value}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "aksi",
            headerName: "Aksi",
            headerAlign: "center",
            align: "center",
            minWidth: 100,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <IconButton
                        disabled={params.row.stok === null}
                        onClick={() => {
                            handleSelectedProducts(
                                params.row.id,
                                params.row.namaBarang,
                                params.row.sku,
                                params.row.harga,
                                params.row.stok,
                                params.row.hpp,
                            );
                        }}
                        sx={{
                            minWidth: "unset",
                            padding: "6px",
                            width: "fit-content",
                        }}
                        // startIcon={<EditIcon />}
                        size="small"
                        color="primary"
                    >
                        {isSelected(params.row.id) ? (
                            <CheckBoxIcon />
                        ) : (
                            <CheckBoxOutlineBlankIcon />
                        )}
                    </IconButton>
                );
            },
        },
    ];

    useEffect(() => {
        if (user) {
            setParamsStok((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));

            setParamsKategori((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));
        }
    }, [ukmIdUser, user]);

    useEffect(() => {
        if (selectedKategori !== null) {
            setParamsStok((prev) => ({
                ...prev,
                kategori: String(selectedKategori.id),
            }));
        } else {
            setParamsStok((prev) => ({
                ...prev,
                kategori: "",
            }));
        }
    }, [selectedKategori]);

    const handleOnPagination = (page: number) => {
        setParamsStok((prev) => ({
            ...prev,
            page: page + 1,
        }));
    };

    const handleOnSizeChange = (size: number) => {
        setParamsStok((prev) => ({ ...prev, size }));
    };

    useEffect(() => {
        addProducts(selectedProducts ?? []);
    }, [addProducts, selectedProducts]);

    const isLoading = statusUser === "loading" || statusKategori === "loading";

    if (isLoading) {
        return (
            <Stack
                display="grid"
                gridTemplateColumns="1fr"
                alignItems="center"
                height={400}
                position="relative"
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <Box className="pilih-item">
            <Box
                ml={-1.1}
                flexDirection="row"
                display="flex"
                alignItems="center"
            >
                <Checkbox
                    onChange={handleClickAllProduct}
                    checked={selectedProducts.length === stokAllProduct?.length}
                />
                <Typography>Pilih Semua</Typography>
            </Box>
            <Box
                display="grid"
                gridTemplateColumns={isTabletScreen ? "1fr" : "1fr 0.5fr"}
                columnGap="1em"
                rowGap={isTabletScreen ? "1em" : undefined}
                marginY={2}
            >
                <TextField
                    placeholder="Cari barang..."
                    size="small"
                    value={search}
                    onChange={handleChangeSearch}
                    variant="outlined"
                    fullWidth
                    onKeyPress={(e) => {
                        const element = e.target as HTMLInputElement;
                        if (e.key === "Enter") {
                            setParamsStok((prev) => ({
                                ...prev,
                                search: element.value,
                            }));
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleSearch}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: {
                            backgroundColor: "white",
                        },
                    }}
                />
                <Autocomplete
                    disablePortal
                    loading={isLoadingKategori}
                    options={kategori?.content ?? []}
                    getOptionLabel={(option) => option.nama}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    size="small"
                    value={selectedKategori}
                    onChange={(_event, newKategori: IOptions | null) => {
                        setSelectedKategori(newKategori);
                    }}
                    // sx={{ width: 300 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Pilih kategori" />
                    )}
                />
            </Box>
            <Box
                sx={{
                    height: 460,
                    width: "100%",
                    backgroundColor: "#ffffff",
                    borderRadius: 1,
                    "& .headerColumn": {
                        backgroundColor: "#E4EEE8",
                        // fontSize: "18px",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: "bold",
                    },
                    "& .MuiDataGrid-columnSeparator": {
                        visibility: "hidden",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                    },
                    "& .MuiDataGrid-iconButtonContainer": {
                        visibility: "visible",
                        width: "0 !important",
                    },
                }}
            >
                <DataGrid
                    rows={stok?.content ?? []}
                    // rows={stok?.content ?? []}
                    components={{
                        ColumnUnsortedIcon: () => {
                            return <ImportExportIcon />;
                        },
                        NoRowsOverlay: NoRowsImage,
                        LoadingOverlay: LoadingTabel,
                    }}
                    columns={columns}
                    paginationMode="server"
                    rowsPerPageOptions={[10]}
                    onPageSizeChange={handleOnSizeChange}
                    onPageChange={handleOnPagination}
                    rowCount={stok?.totalElements ?? 0}
                    page={paramsStok.page - 1}
                    pageSize={paramsStok.size}
                    disableColumnMenu
                    disableSelectionOnClick
                    density="compact"
                    loading={isLoadingStok}
                />
            </Box>
            <Box mt={2}>
                <Typography fontWeight="bold">Barang Terpilih:</Typography>
                <Typography>
                    {selectedProducts.map((li) => li.nama).join(", ")}
                </Typography>
            </Box>
        </Box>
    );
};

export default PilihItemMutasiStep;
